// Theme configuration for SHAHSA application
export interface ThemeColors {
  // Primary colors from the palette
  primary: string;           // #108bc4 - Main brand color
  secondary: string;         // #d2a041 - Accent color (golden)
  dark: string;             // #1d2731 - Dark background
  light: string;            // #f6f6f6 - Light background
  
  // UI element colors
  background: string;        // Main background
  surface: string;          // Card/component background
  text: {
    primary: string;        // Main text color
    secondary: string;      // Secondary text color
    disabled: string;       // Disabled text color
  };
  border: string;           // Border color
  divider: string;          // Divider/separator color
  
  // Interactive elements
  button: {
    primary: string;        // Primary button background
    primaryHover: string;   // Primary button hover
    secondary: string;      // Secondary button background
    secondaryHover: string; // Secondary button hover
    text: string;          // Button text color
  };
  
  // Form elements
  input: {
    background: string;     // Input background
    border: string;         // Input border
    borderFocus: string;    // Input focus border
    placeholder: string;    // Placeholder text color
  };
  
  // Status colors
  success: string;          // Success color
  warning: string;          // Warning color
  error: string;            // Error color
  info: string;             // Info color
  
  // Map and navigation
  map: {
    background: string;     // Map background
    route: string;          // Route line color
    pin: string;            // Location pin color
    grid: string;           // Map grid lines
  };
}

// Light theme (based on the cream/beige theme from the images)
export const lightTheme: ThemeColors = {
  primary: '#108bc4',
  secondary: '#d2a041',
  dark: '#1d2731',
  light: '#f6f6f6',
  
  background: '#f6f6f6',
  surface: '#ffffff',
  text: {
    primary: '#1d2731',
    secondary: '#666666',
    disabled: '#999999',
  },
  border: '#e0e0e0',
  divider: '#f0f0f0',
  
  button: {
    primary: '#108bc4',
    primaryHover: '#0d6b8f',
    secondary: '#d2a041',
    secondaryHover: '#b88a2e',
    text: '#ffffff',
  },
  
  input: {
    background: '#f6f6f6',
    border: '#d9d9d9',
    borderFocus: '#108bc4',
    placeholder: '#bfbfbf',
  },
  
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#108bc4',
  
  map: {
    background: '#f6f6f6',
    route: '#108bc4',
    pin: '#d2a041',
    grid: '#e8e8e8',
  },
};

// Dark theme (based on the dark charcoal theme from the images)
export const darkTheme: ThemeColors = {
  primary: '#108bc4',
  secondary: '#d2a041',
  dark: '#1d2731',
  light: '#f6f6f6',
  
  background: '#0f1419',
  surface: '#1a202c',
  text: {
    primary: '#ffffff',
    secondary: '#e2e8f0',
    disabled: '#718096',
  },
  border: '#2d3748',
  divider: '#2d3748',
  
  button: {
    primary: '#108bc4',
    primaryHover: '#0d6b8f',
    secondary: '#d2a041',
    secondaryHover: '#b88a2e',
    text: '#ffffff',
  },
  
  input: {
    background: '#2d3748',
    border: '#4a5568',
    borderFocus: '#108bc4',
    placeholder: '#a0aec0',
  },
  
  success: '#48bb78',
  warning: '#ed8936',
  error: '#f56565',
  info: '#108bc4',
  
  map: {
    background: '#0f1419',
    route: '#108bc4',
    pin: '#d2a041',
    grid: '#2d3748',
  },
};

// Theme context and provider
export type ThemeMode = 'light' | 'dark';

export interface ThemeContextType {
  theme: ThemeColors;
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

// Utility functions for theme
export const getTheme = (mode: ThemeMode): ThemeColors => {
  return mode === 'dark' ? darkTheme : lightTheme;
};

// CSS variables for easy theming
export const generateCSSVariables = (theme: ThemeColors): string => {
  return `
    :root {
      /* Primary colors */
      --color-primary: ${theme.primary};
      --color-secondary: ${theme.secondary};
      --color-dark: ${theme.dark};
      --color-light: ${theme.light};
      
      /* Background colors */
      --color-background: ${theme.background};
      --color-surface: ${theme.surface};
      
      /* Text colors */
      --color-text-primary: ${theme.text.primary};
      --color-text-secondary: ${theme.text.secondary};
      --color-text-disabled: ${theme.text.disabled};
      
      /* Border colors */
      --color-border: ${theme.border};
      --color-divider: ${theme.divider};
      
      /* Button colors */
      --color-button-primary: ${theme.button.primary};
      --color-button-primary-hover: ${theme.button.primaryHover};
      --color-button-secondary: ${theme.button.secondary};
      --color-button-secondary-hover: ${theme.button.secondaryHover};
      --color-button-text: ${theme.button.text};
      
      /* Input colors */
      --color-input-background: ${theme.input.background};
      --color-input-border: ${theme.input.border};
      --color-input-border-focus: ${theme.input.borderFocus};
      --color-input-placeholder: ${theme.input.placeholder};
      
      /* Status colors */
      --color-success: ${theme.success};
      --color-warning: ${theme.warning};
      --color-error: ${theme.error};
      --color-info: ${theme.info};
      
      /* Map colors */
      --color-map-background: ${theme.map.background};
      --color-map-route: ${theme.map.route};
      --color-map-pin: ${theme.map.pin};
      --color-map-grid: ${theme.map.grid};
    }
  `;
};

export default {
  light: lightTheme,
  dark: darkTheme,
  getTheme,
  generateCSSVariables,
};
